import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, Switch, Tag, Alert, Spin, message, Select, Badge, Empty, Tooltip, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, InfoCircleOutlined, SearchOutlined, GlobalOutlined, ShopOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { getOptionTypeMeta, MODULE_OPTION_TYPES_META, TRANSVERSAL_OPTION_TYPES } from '../constants/moduleOptionTypes';

export interface TModuleOption {
  id?: number;
  module_id: number | null;
  company_id: number | null;
  key?: string | null;
  type: string;
  string_value: string;
  numeric_value?: number | null;
  legal_code?: string | null;
  scope?: string;
  show: boolean;
  is_global?: boolean;
  is_active_for_company?: boolean;
}

export interface ModuleOptionsManagerProps {
  moduleId: number;
  companyId: number;
  adminScope?: string | null;
  allowedTypes?: string[];
  securePost: (url: string, body: any) => Promise<any>;
  session: string;
}

export const ModuleOptionsManager: React.FC<ModuleOptionsManagerProps> = ({
  moduleId,
  companyId,
  adminScope,
  allowedTypes,
  securePost,
  session
}) => {
  const [options, setOptions] = useState<TModuleOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingOption, setSavingOption] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [activeType, setActiveType] = useState<string>('');
  const [typeSearchTerm, setTypeSearchTerm] = useState<string>('');
  const [recordSearchTerm, setRecordSearchTerm] = useState<string>('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOption, setEditingOption] = useState<TModuleOption | null>(null);
  const [form] = Form.useForm();
  const fetchedKeyRef = useRef<string | null>(null);

  const isSystemUser = adminScope === 'system';

  const fetchConfig = useCallback(async (force = false) => {
    if (!moduleId || !companyId) return;
    const currentKey = `${moduleId}_${companyId}_${(allowedTypes || []).join(',')}`;
    if (!force && fetchedKeyRef.current === currentKey) return;
    fetchedKeyRef.current = currentKey;

    setLoading(true);
    try {
      const res = await securePost('/module/config/get', {
        session,
        module: moduleId,
        company: companyId
      });

      let fetchedList: TModuleOption[] = [];
      if (Array.isArray(res)) {
        fetchedList = res;
      } else if (res && Array.isArray(res.module_options)) {
        fetchedList = res.module_options;
      }

      if (allowedTypes && allowedTypes.length > 0) {
        fetchedList = fetchedList.filter(item => allowedTypes.includes(item.type));
      }

      setOptions(fetchedList);
    } catch (err) {
      console.error('Error al cargar opciones del módulo:', err);
      message.error('Error al cargar las opciones del módulo');
    } finally {
      setLoading(false);
    }
  }, [moduleId, companyId, session, allowedTypes, securePost]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const availableTypes = useMemo(() => {
    const typesInDB = Array.from(new Set(options.map(o => o.type)));
    const predefinedTypes = allowedTypes || Object.keys(MODULE_OPTION_TYPES_META);
    const combined = Array.from(new Set([...predefinedTypes, ...typesInDB]));
    return combined;
  }, [options, allowedTypes]);

  useEffect(() => {
    if (availableTypes.length > 0 && (!activeType || !availableTypes.includes(activeType))) {
      setActiveType(availableTypes[0]);
    }
  }, [availableTypes, activeType]);

  const currentMeta = useMemo(() => {
    return getOptionTypeMeta(activeType);
  }, [activeType]);

  // Record counts by type (counting active/available ones)
  const typeCounts = useMemo(() => {
    const map: Record<string, { total: number; active: number }> = {};
    for (const opt of options) {
      if (!map[opt.type]) {
        map[opt.type] = { total: 0, active: 0 };
      }
      map[opt.type].total += 1;
      if (opt.is_active_for_company ?? opt.show) {
        map[opt.type].active += 1;
      }
    }
    return map;
  }, [options]);

  // Filtered types for sidebar search
  const filteredTypes = useMemo(() => {
    if (!typeSearchTerm.trim()) return availableTypes;
    const term = typeSearchTerm.toLowerCase().trim();
    return availableTypes.filter(typeKey => {
      const meta = getOptionTypeMeta(typeKey);
      return (
        typeKey.toLowerCase().includes(term) ||
        meta.label.toLowerCase().includes(term) ||
        meta.description.toLowerCase().includes(term)
      );
    });
  }, [availableTypes, typeSearchTerm]);

  // Filtered records for the current active type
  const filteredOptions = useMemo(() => {
    const list = options.filter(o => o.type === activeType);
    if (!recordSearchTerm.trim()) return list;
    const term = recordSearchTerm.toLowerCase().trim();
    return list.filter(o => {
      return (
        (o.key?.toLowerCase().includes(term)) ||
        (o.string_value?.toLowerCase().includes(term)) ||
        (o.legal_code?.toLowerCase().includes(term)) ||
        (o.numeric_value !== undefined && o.numeric_value !== null && String(o.numeric_value).includes(term))
      );
    });
  }, [options, activeType, recordSearchTerm]);

  const handleToggleActivation = async (record: TModuleOption, checked: boolean) => {
    if (!record.id) return;
    setTogglingId(record.id);
    try {
      // Optimistic update
      setOptions(prev => prev.map(opt => opt.id === record.id ? { ...opt, is_active_for_company: checked } : opt));

      await securePost('/module/company-activation/update', {
        session,
        company_id: companyId,
        option_id: record.id,
        is_active: checked,
        module_id: moduleId
      });
      message.success(`${record.string_value}: ${checked ? 'Activado' : 'Desactivado'} para esta empresa`);
    } catch (err: any) {
      console.error('Error al actualizar activación:', err);
      message.error('Error al actualizar la activación del parámetro');
      // Revert on error
      setOptions(prev => prev.map(opt => opt.id === record.id ? { ...opt, is_active_for_company: !checked } : opt));
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpenCreate = () => {
    setEditingOption(null);
    form.resetFields();
    const firstPredefined = currentMeta.predefinedOptions?.[0];
    form.setFieldsValue({
      type: activeType,
      show: true,
      scope: currentMeta.defaultScope || 'company',
      numeric_value: 0,
      is_global: false,
      ...(firstPredefined
        ? {
            predefined_key: firstPredefined.key,
            key: firstPredefined.key,
            string_value: firstPredefined.label
          }
        : {})
    });
    setModalVisible(true);
  };

  const handleOpenEdit = (record: TModuleOption) => {
    setEditingOption(record);
    const isGlobal = record.is_global || !record.company_id || record.scope === 'system';
    form.setFieldsValue({
      key: record.key,
      string_value: record.string_value,
      numeric_value: record.numeric_value ?? 0,
      legal_code: record.legal_code,
      scope: isGlobal ? 'system' : (record.scope || 'company'),
      show: record.show ?? true,
      is_global: isGlobal,
      predefined_key: record.key
    });
    setModalVisible(true);
  };

  const handleSaveOption = async (values: any) => {
    setSavingOption(true);
    try {
      let finalKey = values.key;
      let finalStringValue = values.string_value;

      if (currentMeta.predefinedOptions && currentMeta.predefinedOptions.length > 0) {
        const found = currentMeta.predefinedOptions.find(opt => opt.key === values.predefined_key);
        if (found) {
          finalKey = found.key;
          finalStringValue = found.label;
        }
      }

      const isTransversal = currentMeta.managementMode === 'global' || TRANSVERSAL_OPTION_TYPES.includes(activeType);
      const isCreatingGlobal = isSystemUser && (values.is_global === true || values.scope === 'system');

      const payloadOption: any = {
        ...(editingOption?.id ? { id: editingOption.id } : {}),
        module_id: (isCreatingGlobal || isTransversal) ? null : moduleId,
        company_id: isCreatingGlobal ? null : companyId,
        type: activeType,
        key: finalKey || finalStringValue?.substring(0, 10).toUpperCase(),
        string_value: finalStringValue,
        numeric_value: currentMeta.allowedFields.includes('numeric_value') ? (values.numeric_value ?? 0) : 0,
        legal_code: currentMeta.allowedFields.includes('legal_code') ? (values.legal_code || '') : '',
        scope: isCreatingGlobal ? 'system' : 'company',
        show: values.show ?? true,
        is_global: isCreatingGlobal
      };

      const res = await securePost('/module/config/update', {
        session,
        uidModule: moduleId,
        uidCompany: companyId,
        data: [payloadOption]
      });

      if (res && typeof res === 'object' && res.status >= 400) {
        throw new Error(res.message || `Error ${res.status}: No se pudo guardar el parámetro`);
      }

      message.success(editingOption ? 'Parámetro actualizado exitosamente' : 'Parámetro creado exitosamente');
      setModalVisible(false);
      fetchConfig(true);
    } catch (err: any) {
      console.error('Error al guardar parámetro:', err);
      message.error(err?.message || 'Error al guardar el parámetro');
    } finally {
      setSavingOption(false);
    }
  };

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDeleteOption = async (record: TModuleOption) => {
    if (!record.id) return;
    setDeletingId(record.id);
    try {
      const res = await securePost('/module/config/delete', {
        session,
        id: record.id,
        company: companyId,
        module: moduleId
      });

      if (res && typeof res === 'object' && res.status === 409) {
        // Está en uso: ofrecer desactivar/ocultar
        Modal.confirm({
          title: `No se puede eliminar "${record.string_value}"`,
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
          content: (
            <div>
              <p style={{ marginTop: 8 }}>
                {res.message || 'Este parámetro está en uso en artículos o registros del sistema y no puede eliminarse para proteger la integridad de los datos históricos.'}
              </p>
              <p style={{ color: '#1677ff', fontWeight: 500, marginTop: 8 }}>
                ¿Deseas desactivarlo / ocultarlo para que no aparezca en nuevas selecciones?
              </p>
            </div>
          ),
          okText: 'Ocultar / Desactivar',
          cancelText: 'Cerrar',
          okType: 'primary',
          onOk: async () => {
            const isGlobal = record.is_global || !record.company_id;
            if (isGlobal) {
              await handleToggleActivation(record, false);
            } else {
              await securePost('/module/config/update', {
                session,
                uidModule: moduleId,
                uidCompany: companyId,
                data: [{ id: record.id, show: false }]
              });
              message.success(`Parámetro "${record.string_value}" ocultado exitosamente`);
              fetchConfig(true);
            }
          }
        });
        return;
      }

      if (res && typeof res === 'object' && res.status >= 400) {
        throw new Error(res.message || `Error ${res.status}: No se pudo eliminar el parámetro`);
      }

      message.success(res?.message || 'Parámetro eliminado exitosamente');
      fetchConfig(true);
    } catch (err: any) {
      console.error('Error al eliminar parámetro:', err);
      message.error(err?.message || 'Error al eliminar el parámetro');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'Clave (Key)',
      dataIndex: 'key',
      key: 'key',
      width: 120,
      render: (key: string) => <Tag color="blue">{key || '-'}</Tag>
    },
    {
      title: 'Nombre / Descripción',
      dataIndex: 'string_value',
      key: 'string_value',
      render: (val: string) => <strong>{val}</strong>
    },
    {
      title: 'Origen',
      key: 'origin',
      width: 130,
      render: (_: any, record: TModuleOption) => {
        const isGlobal = record.is_global || !record.company_id;
        return isGlobal ? (
          <Tag icon={<GlobalOutlined />} color="geekblue">
            Global
          </Tag>
        ) : (
          <Tag icon={<ShopOutlined />} color="green">
            Personalizado
          </Tag>
        );
      }
    },
    ...(currentMeta.allowedFields.includes('numeric_value')
      ? [
          {
            title: 'Valor Numérico',
            dataIndex: 'numeric_value',
            key: 'numeric_value',
            width: 110,
            render: (val: number) => (val ?? '-')
          }
        ]
      : []),
    ...(currentMeta.allowedFields.includes('legal_code')
      ? [
          {
            title: 'Código Fiscal',
            dataIndex: 'legal_code',
            key: 'legal_code',
            width: 120,
            render: (val: string) => (val ? <Tag color="green">{val}</Tag> : '-')
          }
        ]
      : []),
    ...(isSystemUser
      ? [
          {
            title: 'Alcance (Scope)',
            dataIndex: 'scope',
            key: 'scope',
            width: 120,
            render: (scope: string) =>
              scope === 'system' ? (
                <Tag color="purple">Sistema</Tag>
              ) : (
                <Tag color="cyan">Empresa</Tag>
              )
          }
        ]
      : []),
    {
      title: 'Activo en Empresa',
      key: 'activation',
      width: 140,
      render: (_: any, record: TModuleOption) => {
        const isGlobal = record.is_global || !record.company_id;
        const isActive = isGlobal ? (record.is_active_for_company ?? false) : record.show;

        if (isGlobal) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Switch
                size="small"
                checked={isActive}
                loading={togglingId === record.id}
                onChange={checked => handleToggleActivation(record, checked)}
              />
              <span style={{ fontSize: 12, color: isActive ? '#52c41a' : '#8c8c8c', fontWeight: 500 }}>
                {isActive ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          );
        }

        return isActive ? (
          <Tag color="success">Visible</Tag>
        ) : (
          <Tag color="default">Oculto</Tag>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 130,
      render: (_: any, record: TModuleOption) => {
        const isGlobal = record.is_global || !record.company_id;
        const canEdit = isSystemUser || !isGlobal;

        if (!canEdit) {
          return (
            <Tooltip title="Las opciones globales del catálogo solo pueden ser modificadas por SuperAdmin. Puedes activarla o desactivarla para tu empresa con el switch.">
              <span style={{ color: '#bfbfbf', fontSize: 12 }}>Estándar</span>
            </Tooltip>
          );
        }

        return (
          <Space size="small">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            >
              Editar
            </Button>
            <Popconfirm
              title={`¿Eliminar "${record.string_value}"?`}
              description="Si está en uso en artículos de inventario, el sistema evitará la eliminación para proteger datos históricos."
              okText="Eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true, loading: deletingId === record.id }}
              onConfirm={() => handleDeleteOption(record)}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deletingId === record.id}
              />
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Spin size="large" tip="Cargando parámetros del módulo..." />
      </div>
    );
  }

  if (availableTypes.length === 0) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        <Empty
          description="Este módulo no tiene tipos de parámetros asignados."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 8 }}>
            Dirígete a la pestaña <strong>Tipos Asignados al Módulo</strong> para activar las opciones que requiere este módulo.
          </div>
        </Empty>
      </div>
    );
  }

  const isGlobalOnlyType = currentMeta.managementMode === 'global';

  return (
    <div style={{ display: 'flex', gap: 16, minHeight: 480 }}>
      {/* Sidebar de Tipos de Parámetros */}
      <div
        style={{
          width: 270,
          minWidth: 270,
          maxWidth: 270,
          borderRight: '1px solid #f0f0f0',
          paddingRight: 12,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <Input
            placeholder="Buscar tipo..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={typeSearchTerm}
            onChange={e => setTypeSearchTerm(e.target.value)}
            allowClear
            size="small"
            style={{ borderRadius: 6 }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 4px 8px 4px',
            fontSize: 12,
            color: '#8c8c8c',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          <span>Opciones ({filteredTypes.length})</span>
          <span>Activos</span>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 280px)',
            paddingRight: 4
          }}
        >
          {filteredTypes.length === 0 ? (
            <div style={{ padding: '24px 8px', textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>
              Sin resultados
            </div>
          ) : (
            filteredTypes.map(typeKey => {
              const meta = getOptionTypeMeta(typeKey);
              const isSelected = activeType === typeKey;
              const counts = typeCounts[typeKey] || { total: 0, active: 0 };

              return (
                <div
                  key={typeKey}
                  onClick={() => setActiveType(typeKey)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    marginBottom: 6,
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: isSelected ? '#e6f4ff' : '#ffffff',
                    border: isSelected ? '1px solid #91caff' : '1px solid #f0f0f0',
                    boxShadow: isSelected ? '0 2px 4px rgba(22, 119, 255, 0.08)' : 'none'
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '#fafafa';
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '#ffffff';
                  }}
                >
                  <div style={{ overflow: 'hidden', paddingRight: 8, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? '#1677ff' : '#262626',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={meta.label}
                    >
                      {meta.label}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
                      <Tag
                        bordered={false}
                        color={isSelected ? 'blue' : 'default'}
                        style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px' }}
                      >
                        {typeKey}
                      </Tag>
                      <Tag
                        bordered={false}
                        color={meta.managementMode === 'global' ? 'purple' : 'green'}
                        style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px' }}
                      >
                        {meta.managementMode === 'global' ? 'Global' : 'Híbrido'}
                      </Tag>
                    </div>
                  </div>

                  <Badge
                    count={`${counts.active}/${counts.total}`}
                    style={{
                      backgroundColor: counts.active > 0 ? (isSelected ? '#1677ff' : '#52c41a') : '#d9d9d9',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                      boxShadow: 'none'
                    }}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Contenido Principal / Tabla de Registros */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Cabecera del Parámetro Activo */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            paddingBottom: 12,
            borderBottom: '1px solid #f0f0f0'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1f1f1f' }}>
                {currentMeta.label}
              </h3>
              <Tag color="blue">{activeType}</Tag>
              <Tag color={isGlobalOnlyType ? 'purple' : 'green'}>
                {isGlobalOnlyType ? 'Gestión Global (Catálogo)' : 'Gestión Híbrida (Global + Propio)'}
              </Tag>
            </div>
          </div>

          {(isSystemUser || !isGlobalOnlyType) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
            >
              {isGlobalOnlyType ? 'Agregar al Catálogo Global' : 'Agregar Parámetro Propio'}
            </Button>
          )}
        </div>

        {/* Descripción del Parámetro */}
        {currentMeta && (
          <Alert
            message={currentMeta.description}
            description={
              isGlobalOnlyType
                ? 'Este parámetro se gestiona con el catálogo global estándar. Activa o desactiva con el switch las opciones que utilizará esta empresa.'
                : 'Este parámetro admite opciones estándar del catálogo global y opciones personalizadas creadas específicamente para tu empresa.'
            }
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16, borderRadius: 6 }}
          />
        )}

        {/* Barra de Filtro de Registros */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13, color: '#595959' }}>
            Opciones Disponibles ({filteredOptions.length})
          </span>
          <Input
            placeholder="Buscar en opciones..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={recordSearchTerm}
            onChange={e => setRecordSearchTerm(e.target.value)}
            allowClear
            size="small"
            style={{ width: 220, borderRadius: 6 }}
          />
        </div>

        {/* Tabla */}
        <Table
          dataSource={filteredOptions}
          columns={columns}
          rowKey={(item, index) => `${item.id || index}`}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          locale={{
            emptyText: (
              <div style={{ padding: '24px 0' }}>
                <Empty
                  description={
                    recordSearchTerm
                      ? 'No hay registros que coincidan con la búsqueda'
                      : `Aún no hay parámetros configurados para "${currentMeta.label}"`
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  {!recordSearchTerm && (isSystemUser || !isGlobalOnlyType) && (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={handleOpenCreate}
                      style={{ marginTop: 8 }}
                    >
                      {isGlobalOnlyType ? 'Crear opción global' : 'Crear primer parámetro'}
                    </Button>
                  )}
                </Empty>
              </div>
            )
          }}
        />
      </div>

      {/* Modal para Crear / Editar Opción */}
      <Modal
        title={editingOption ? `Editar: ${editingOption.string_value}` : `Nuevo Parámetro (${currentMeta.label})`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={savingOption}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSaveOption}>
          {currentMeta.predefinedOptions && currentMeta.predefinedOptions.length > 0 ? (
            <Form.Item
              name="predefined_key"
              label="Seleccionar Opción Predefinida"
              rules={[{ required: true, message: 'Seleccione una opción predefinida' }]}
            >
              <Select
                disabled={savingOption}
                onChange={selectedKey => {
                  const found = currentMeta.predefinedOptions?.find(opt => opt.key === selectedKey);
                  if (found) {
                    form.setFieldsValue({
                      key: found.key,
                      string_value: found.label
                    });
                  }
                }}
              >
                {currentMeta.predefinedOptions.map(opt => (
                  <Select.Option key={opt.key} value={opt.key}>
                    <strong>{opt.label}</strong> ({opt.key})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="string_value"
                label="Nombre / Valor Visible"
                rules={[{ required: true, message: 'Ingrese el nombre o etiqueta' }]}
              >
                <Input placeholder="Ej. IVA 19%, Kilogramo, Efectivo" disabled={savingOption} />
              </Form.Item>

              <Form.Item name="key" label="Clave Única (Key)">
                <Input placeholder="Ej. IV19, KGR, CASH (Se genera si se deja en blanco)" disabled={savingOption} />
              </Form.Item>
            </>
          )}

          {currentMeta.allowedFields.includes('numeric_value') && (
            <Form.Item name="numeric_value" label="Valor Numérico (Opcional)">
              <InputNumber style={{ width: '100%' }} placeholder="Ej. 19.00" precision={2} disabled={savingOption} />
            </Form.Item>
          )}

          {currentMeta.allowedFields.includes('legal_code') && (
            <Form.Item name="legal_code" label="Código Legal / Fiscal (DIAN/ISO)">
              <Input placeholder="Ej. 01, KGM, 10" disabled={savingOption} />
            </Form.Item>
          )}

          {isSystemUser && (
            <Form.Item name="scope" label="Nivel / Alcance" rules={[{ required: true }]}>
              <Select disabled={savingOption}>
                <Select.Option value="company">Empresa (Específico de la Empresa Actual)</Select.Option>
                <Select.Option value="system">Global (Catálogo Maestro del Sistema)</Select.Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item name="show" label="Visible en la aplicación" valuePropName="checked">
            <Switch disabled={savingOption} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
